import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';

const DEFAULT_FILTERS = {
  source: '',
  carc_code: '',
  error_code: '',
  triage_status: '',
  date_from: '',
  date_to: '',
  search: '',
};

/**
 * Centralises the Denials dashboard state — summary, paginated list,
 * filters, triage actions, CSV export. Keeps the page itself a thin shell.
 */
export function useDenials() {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [listLoading, setListLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [exporting, setExporting] = useState(false);

  // Build query params from filters (omit empties)
  const buildParams = useCallback(
    (extra = {}) => {
      const params = { ...extra };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      return params;
    },
    [filters],
  );

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await api.get('/denials/summary', {
        params: buildParams(),
      });
      setSummary(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load denial metrics');
    } finally {
      setSummaryLoading(false);
    }
  }, [buildParams]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await api.get('/denials/list', {
        params: buildParams({ page, page_size: pageSize }),
      });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load denial list');
    } finally {
      setListLoading(false);
    }
  }, [buildParams, page, pageSize]);

  // Initial load + whenever filters change → reset to page 1, refetch both.
  useEffect(() => {
    setPage(1);
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.source,
    filters.carc_code,
    filters.error_code,
    filters.triage_status,
    filters.date_from,
    filters.date_to,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const updateFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));
  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/denials/export', {
        params: buildParams(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '');
      link.setAttribute('download', `denials_${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const triageClaim = async (claimId, action, notes) => {
    try {
      const res = await api.post(`/claims/${claimId}/triage`, { action, notes: notes || null });
      const verbs = { fix: 'reopened for editing', void: 'voided', resolve: 'marked as resolved' };
      toast.success(`${res.data.claim_number} ${verbs[action]}`);
      // refresh both summary and list
      fetchSummary();
      fetchList();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Triage failed');
      return false;
    }
  };

  return {
    summary, summaryLoading,
    rows, total, totalPages, page, setPage, pageSize, listLoading,
    filters, updateFilter, resetFilters,
    exportCsv, exporting,
    triageClaim,
    refresh: () => { fetchSummary(); fetchList(); },
  };
}
